import { useId, useRef, useState } from 'react';
import { productShots } from '../data/site';
import { ProductShot } from './Common';

/** Real product captures with keyboard navigation and an accessible full-size view. */
export function ProductExplorer({
  priority = false,
  limit,
}: {
  priority?: boolean;
  limit?: number;
}) {
  const shots = limit ? productShots.slice(0, limit) : productShots;
  const [selected, setSelected] = useState(0);
  const id = useId();
  const tabs = useRef<HTMLDivElement>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const shot = shots[selected];

  function select(index: number) {
    setSelected(index);
    (tabs.current?.children[index] as HTMLButtonElement)?.focus();
  }

  return (
    <div className="product-explorer">
      <div className="explorer-toolbar">
        <span className="explorer-label">Explore the workspace</span>
        <span className="preview-label">Demo data</span>
      </div>
      <div className="explorer-tabs" role="tablist" aria-label="Application screens" ref={tabs}>
        {shots.map((item, index) => (
          <button
            key={item.slug}
            type="button"
            role="tab"
            id={`${id}-tab-${index}`}
            aria-selected={selected === index}
            aria-controls={`${id}-panel`}
            tabIndex={selected === index ? 0 : -1}
            onClick={() => setSelected(index)}
            onKeyDown={event => {
              let next: number;
              if (event.key === 'ArrowRight') next = (selected + 1) % shots.length;
              else if (event.key === 'ArrowLeft')
                next = (selected - 1 + shots.length) % shots.length;
              else if (event.key === 'Home') next = 0;
              else if (event.key === 'End') next = shots.length - 1;
              else return;
              event.preventDefault();
              select(next);
            }}
          >
            {item.title}
          </button>
        ))}
      </div>
      <div
        id={`${id}-panel`}
        role="tabpanel"
        aria-labelledby={`${id}-tab-${selected}`}
        tabIndex={0}
      >
        <button
          className="explorer-preview"
          type="button"
          onClick={() => dialog.current?.showModal()}
          aria-label={`Enlarge ${shot.title} screenshot`}
        >
          <ProductShot key={shot.slug} slug={shot.slug} alt={shot.caption} priority={priority} />
          <span className="enlarge-hint">
            View full screen <span aria-hidden="true">↗</span>
          </span>
        </button>
        <div className="explorer-caption">
          <span className="screen-number">
            {String(selected + 1).padStart(2, '0')} / {String(shots.length).padStart(2, '0')}
          </span>
          <p>{shot.caption}</p>
        </div>
      </div>
      <dialog
        className="screenshot-dialog"
        ref={dialog}
        aria-labelledby={`${id}-dialog-title`}
        onClick={event => {
          if (event.target === event.currentTarget) dialog.current?.close();
        }}
      >
        <div className="dialog-toolbar">
          <strong id={`${id}-dialog-title`}>{shot.title}</strong>
          <form method="dialog">
            <button className="button secondary" autoFocus>
              Close <span aria-hidden="true">×</span>
            </button>
          </form>
        </div>
        <ProductShot slug={shot.slug} alt={shot.caption} priority />
        <p>{shot.caption}</p>
      </dialog>
    </div>
  );
}
